/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"

import { OrganizationViewModel } from "@/features/organization/organization-viewmodel"
import { userRowLocation } from "@/features/user/user-detail-location"
import { UserListViewModel } from "@/features/user/user-list-viewmodel"
import { useViewModel } from "../app/app-hooks"
import { Card } from "../primitives/card"
import { Skeleton } from "../primitives/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../primitives/table"
import { UserRow } from "./user-row"

interface Props {
    /**
     * Id of the element that names the table.
     */
    labelledBy: string
}

/**
 * Lists the current page of users of the selected organization.
 *
 * Shows skeleton rows while the users load, and an empty row when there is
 * nothing to list.
 */
export const UserTable = observer(({ labelledBy }: Props) => {
    const listVM = useViewModel(UserListViewModel)
    const orgVM = useViewModel(OrganizationViewModel)

    const selectedOrgId = orgVM.selectedOrgId

    return (
        <Card className="py-0">
            <Table aria-labelledby={labelledBy} aria-busy={listVM.isLoadingUsers}>
                <TableHeader>
                    <TableRow>
                        <TableHead>Name</TableHead>
                        {/* Email takes the spare width; Status and Type shrink to their badges. */}
                        <TableHead className="w-full">Email</TableHead>
                        <TableHead className="w-px">Status</TableHead>
                        <TableHead className="w-px">Type</TableHead>
                        <TableHead className="min-w-64">Roles</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {listVM.isLoadingUsers
                        ? renderSkeletons()
                        : listVM.pagedUsers.length > 0 && selectedOrgId !== "none"
                          ? listVM.pagedUsers.map((user) => (
                                <UserRow key={user.id} user={user} to={userRowLocation(user, selectedOrgId)} />
                            ))
                          : renderEmptyRow(listVM.hasUsers)}
                </TableBody>
            </Table>
        </Card>
    )
})

/**
 * Renders the row shown when the table has no users to list.
 *
 * @param hasUsers - Whether the organization has any users, so the filters hid them all.
 */
function renderEmptyRow(hasUsers: boolean) {
    return (
        <TableRow className="hover:bg-transparent">
            <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                {hasUsers ? "No users found matching filters." : "No users found."}
            </TableCell>
        </TableRow>
    )
}

function renderSkeletons() {
    return Array.from({ length: 10 }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the rows are identical and never reorder
        <TableRow key={i} aria-hidden className="hover:bg-transparent">
            {Array.from({ length: 5 }, (_, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: the cells are identical and never reorder
                <TableCell key={j}>
                    <Skeleton className="h-5" />
                </TableCell>
            ))}
        </TableRow>
    ))
}
